/**
 * LearnerActivitySummary
 *
 * Collapsible above-the-catalog panel that surfaces what the learner
 * is doing right now and what they've finished. Five tabs:
 *
 *   • Enrolled     — courses + learning paths in flight (NotStarted /
 *                    InProgress)
 *   • Completed    — same set, finished
 *   • Assignments  — admin-assigned content currently in flight
 *   • Certificates — earned certificates with download links
 *   • Transcript   — formal table of completions with CSV export
 *
 * Two layers of accordion:
 *   1. The whole panel collapses / expands via the chevron next to the
 *      "Your activity" header. State persists across renders.
 *   2. Each content row expands inline to show thumbnail, description,
 *      instructor, progress bar, and a Continue button. Course details
 *      are lazy-fetched per courseId so the panel mount stays cheap.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Certificate,
  Course,
  CourseEnrollment,
  CourseType,
  EnrollmentStatus,
} from '../../apis/api-client';
import { certificatesClient, coursesClient, enrollmentsClient } from '../../apis/client-instances';
import {
  Box,
  Card,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from '../ui';
import {
  BookmarkIcon,
  CertificateIcon,
  ChevronRightIcon,
  FileTextIcon,
  PlayCircleIcon,
  SchoolIcon,
} from '../icons';
import { alert, getFileUrl } from '../../hooks/function';
import { formatDate } from '../../utils/roleHelpers';
import { useSelector } from 'react-redux';
import { selectAuthorEntities, useEnsureAuthors } from '../../features/lookups/lookupsSlice';
import EnrollmentActivityRow from './learnerActivity/EnrollmentActivityRow';
import LearnerActivityCertificates from './learnerActivity/LearnerActivityCertificates';
import LearnerActivityTranscript from './learnerActivity/LearnerActivityTranscript';

export interface LearnerActivitySummaryProps {
  userId?: number;
}

type ActivityTab = 'enrolled' | 'completed' | 'assignments' | 'certificates' | 'transcript';

export default function LearnerActivitySummary({ userId }: LearnerActivitySummaryProps) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  // Load the shared author directory so instructor names resolve in rows.
  useEnsureAuthors();
  const authorsById = useSelector(selectAuthorEntities);
  const [tab, setTab] = useState<ActivityTab>('assignments');
  // Panel-level collapse — closed by default so the catalog sits at
  // the top of the fold. Learners click the header to expand when
  // they want to see what's in progress.
  const [panelOpen, setPanelOpen] = useState(false);
  // Per-row expansion — only one row open at a time so the panel
  // doesn't grow unbounded.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const [enrollments, setEnrollments] = useState<CourseEnrollment[]>([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(true);

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [certificatesLoading, setCertificatesLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  // Per-courseId cache of resolved Course objects + their SAS thumbnails.
  // Bulk-fetched on panel mount so the row chips/thumbnails read
  // correctly from the start (the enrollment endpoint doesn't carry
  // courseType, so without this learning paths get mislabeled).
  const [courseCache, setCourseCache] = useState<Record<number, Course>>({});
  const [thumbCache, setThumbCache] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    // Pulled out into a refetcher so the window-focus listener below
    // can call it directly — coming back to the dashboard tab after
    // finishing a lesson elsewhere pulls fresh progress numbers
    // without forcing a full page reload.
    const fetchEnrollments = async () => {
      try {
        const result = await enrollmentsClient.searchUserEnrollments(userId, undefined, 1, 200);
        if (!cancelled) setEnrollments(result.items ?? []);
      } catch {
        if (!cancelled) setEnrollments([]);
      } finally {
        if (!cancelled) setEnrollmentsLoading(false);
      }
    };

    fetchEnrollments();

    (async () => {
      try {
        const result = await certificatesClient.getMyCertificates();
        if (!cancelled) setCertificates(result || []);
      } catch {
        if (!cancelled) setCertificates([]);
      } finally {
        if (!cancelled) setCertificatesLoading(false);
      }
    })();

    const handleFocus = () => {
      fetchEnrollments();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', handleFocus);
    };
  }, [userId]);

  // Content type for a row — carried on the enrollment itself
  // (server-computed), so collapsed rows label correctly with no fetch.
  // Falls back to the lazily-loaded Course cache for older payloads.
  const resolveType = (e: CourseEnrollment): CourseType | undefined =>
    e.courseType ?? (e.courseId ? courseCache[e.courseId]?.courseType : undefined);

  // Partition: enrolled (not done) vs completed. Both contain courses
  // and learning paths together — no separate paths tab. Assignments
  // is its own slice filtered to `isAssigned` rows that aren't done.
  const { enrolledRows, completedRows, assignmentRows } = useMemo(() => {
    const enrolled: CourseEnrollment[] = [];
    const completed: CourseEnrollment[] = [];
    const assignments: CourseEnrollment[] = [];
    for (const e of enrollments) {
      if (e.enrollmentStatus === EnrollmentStatus.Completed) {
        completed.push(e);
      } else {
        enrolled.push(e);
        if (e.isAssigned) assignments.push(e);
      }
    }
    return { enrolledRows: enrolled, completedRows: completed, assignmentRows: assignments };
  }, [enrollments]);

  // Lazy per-row loader — fires only when a row expands, so the panel
  // mounts with zero course/lesson fetches. Pulls the full Course for the
  // expanded description/instructor/thumbnail (cached so a re-expand
  // doesn't refetch). Task counts no longer load here — they ride inline
  // on the enrollment (completedTaskCount / totalTaskCount).
  const loadRowDetails = async (enrollment: CourseEnrollment) => {
    const courseId = enrollment.courseId;
    if (!courseId || courseCache[courseId]) return;
    try {
      const course = await coursesClient.getCourseById(courseId);
      setCourseCache((cur) => ({ ...cur, [courseId]: course }));
      const thumbnailRef = (course as any).thumbnailUrl || course.thumbnail;
      if (thumbnailRef) {
        const thumb = await getFileUrl(thumbnailRef).catch(() => null);
        if (thumb) setThumbCache((cur) => ({ ...cur, [courseId]: thumb }));
      }
    } catch {
      /* tolerate — the collapsed row already has type + progress */
    }
  };

  const handleToggleRow = (key: string, enrollment: CourseEnrollment) => {
    const next = expandedKey === key ? null : key;
    setExpandedKey(next);
    if (next) loadRowDetails(enrollment);
  };

  const openCourse = async (enrollment: CourseEnrollment) => {
    if (!enrollment.courseId) return;
    const slug = enrollment.courseSlug ?? courseCache[enrollment.courseId]?.slug;
    if (slug) {
      navigate(`/courses/${slug}`);
      return;
    }
    try {
      const course = await coursesClient.getCourseById(enrollment.courseId);
      if (course?.slug) navigate(`/courses/${course.slug}`);
      else navigate(`/course/${enrollment.courseId}`);
    } catch {
      navigate(`/course/${enrollment.courseId}`);
    }
  };

  const handleDownloadCertificate = async (cert: Certificate) => {
    setDownloadingId(cert.id);
    try {
      const url = await getFileUrl(cert.imageFilename);
      if (!url) {
        alert(enqueueSnackbar, 'Certificate file unavailable', 'error');
        return;
      }
      const slug = (cert.courseTitle || 'course').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const name = (cert.studentName || 'student').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `certificate-${slug}-${name}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      alert(enqueueSnackbar, 'Failed to download certificate', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleExportTranscript = () => {
    if (completedRows.length === 0) return;
    const header = ['Course', 'Type', 'Started', 'Completed', 'Progress'];
    const rows = completedRows.map((e) => [
      e.courseTitle || `Course #${e.courseId}`,
      (e as any).courseType === CourseType.LearningPath
        ? 'Learning Path'
        : (e as any).courseType === CourseType.External
          ? 'Event'
          : 'Course',
      e.startedAt ? formatDate(e.startedAt) : '',
      e.completedAt ? formatDate(e.completedAt) : '',
      `${Math.round(e.progressPercentage ?? 100)}%`,
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `transcript-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!userId) return null;

  // Resolve the per-row data off the local caches and hand a fully-
  // resolved set of props to the presentational row. All fetching /
  // caching stays here in the orchestrator.
  const renderEnrollmentRow = (e: CourseEnrollment) => {
    const key = String(e.id ?? `${e.userId}-${e.courseId}`);
    const course = e.courseId ? courseCache[e.courseId] : undefined;
    // Resolve the instructor from the shared author directory. If the
    // course or directory hasn't loaded yet, the author renders blank
    // for this tick and fills in on the next render.
    const authorId = (course as any)?.authorId;
    const author = authorId != null ? authorsById[authorId] : undefined;
    return (
      <EnrollmentActivityRow
        key={key}
        enrollment={e}
        resolvedType={resolveType(e)}
        isExpanded={expandedKey === key}
        course={course}
        thumb={e.courseId ? thumbCache[e.courseId] : undefined}
        author={author}
        tasks={{ total: e.totalTaskCount ?? 0, done: e.completedTaskCount ?? 0 }}
        onToggle={() => handleToggleRow(key, e)}
        onOpenCourse={() => openCourse(e)}
      />
    );
  };

  const renderEmptyOrLoading = (
    rows: CourseEnrollment[],
    loading: boolean,
    emptyMessage: string,
  ) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (rows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          {emptyMessage}
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {rows.map(renderEnrollmentRow)}
      </Box>
    );
  };

  return (
    <Card
      sx={{
        p: 0,
        mb: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
      }}
    >
      {/* Panel header — clickable to collapse / expand the whole panel.
          Permanent sky-blue wash (used to be hover-only) so the
          activity rail reads as its own surface against the white
          page below. Tighter vertical padding shortens the band. */}
      <Box
        onClick={() => setPanelOpen((v) => !v)}
        sx={{
          px: 3,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          bgcolor: 'rgba(var(--brand-rgb), 0.08)',
          '&:hover': { bgcolor: 'rgba(var(--brand-rgb), 0.14)' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            Your activity
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {assignmentRows.length} assignment{assignmentRows.length === 1 ? '' : 's'} ·{' '}
            {enrolledRows.length} enrolled · {completedRows.length} completed ·{' '}
            {certificates.length} certificate{certificates.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <ChevronRightIcon
          sx={{
            color: 'text.secondary',
            transform: panelOpen ? 'rotate(90deg)' : 'rotate(0)',
            transition: 'transform 150ms ease',
          }}
        />
      </Box>

      {panelOpen && (
        <>
          <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs
              value={tab}
              onChange={(_e, v) => {
                setTab(v);
                setExpandedKey(null);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                value="assignments"
                label={`Assignments (${assignmentRows.length})`}
                icon={<BookmarkIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
              />
              <Tab
                value="enrolled"
                label={`Enrolled (${enrolledRows.length})`}
                icon={<PlayCircleIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
              />
              <Tab
                value="completed"
                label={`Completed (${completedRows.length})`}
                icon={<SchoolIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
              />
              <Tab
                value="certificates"
                label={`Certificates (${certificates.length})`}
                icon={<CertificateIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
              />
              <Tab
                value="transcript"
                label={`Transcript (${completedRows.length})`}
                icon={<FileTextIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          <Box sx={{ p: 3, maxHeight: 480, overflowY: 'auto' }}>
            {/* Enrolled — courses + paths together, not done. */}
            {tab === 'enrolled' &&
              renderEmptyOrLoading(
                enrolledRows,
                enrollmentsLoading,
                'Nothing in progress yet — browse the catalog below to enroll.',
              )}

            {/* Completed — done content, expandable details. */}
            {tab === 'completed' &&
              renderEmptyOrLoading(
                completedRows,
                enrollmentsLoading,
                'No completed enrollments yet.',
              )}

            {/* Assignments — admin-assigned + currently in flight. */}
            {tab === 'assignments' &&
              renderEmptyOrLoading(
                assignmentRows,
                enrollmentsLoading,
                'No assignments in progress.',
              )}

            {/* Certificates — flat list with download icons. */}
            {tab === 'certificates' && (
              <LearnerActivityCertificates
                certificates={certificates}
                loading={certificatesLoading}
                downloadingId={downloadingId}
                onDownload={handleDownloadCertificate}
              />
            )}

            {/* Transcript — formal table view with CSV export. */}
            {tab === 'transcript' && (
              <LearnerActivityTranscript
                completedRows={completedRows}
                loading={enrollmentsLoading}
                onExport={handleExportTranscript}
                onOpenCourse={openCourse}
              />
            )}
          </Box>
        </>
      )}
    </Card>
  );
}
